import {useMemo, type FC} from 'react'
import {EvaluateStatusFilterProvider} from './providers/evaluate-status'
import {RulesetFilterProvider} from './providers/ruleset'
import {UserFilterProvider} from './providers/user'
import {TimePeriodFilterProvider} from './providers/time-period'
import {RuleStatusFilterProvider} from './providers/rule-status'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import {OrgFilterProvider, RepositoryFilterProvider} from '@github-ui/filter/providers'
import {ShaFilterProvider} from './providers/sha'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {
  GenericFilterBar2,
  extractAsUser,
  getLogin,
  getName,
  timePeriodField,
  repositoryField,
  organizationField,
  timePeriodQueryField,
  orgQueryField,
  repositoryQueryField,
  singleValueFilterTypes,
  type FilterFieldConfig,
  type QueryFieldConfig,
} from '../GenericFilterBar2'
import type {FilterProvider} from '@github-ui/filter'

const insightsFieldConfigs: Array<FilterFieldConfig<InsightsFilter>> = [
  {key: 'actor', queryKey: 'user', getValue: getLogin},
  timePeriodField,
  {key: 'ruleset', queryKey: 'ruleset', getValue: getName, quoted: true},
  {key: 'branch', queryKey: 'branch'},
  {key: 'ruleStatus', queryKey: 'rule-status'},
  {key: 'evaluateStatus', queryKey: 'evaluate-status'},
  repositoryField,
  organizationField,
]

const createInsightsQueryFieldConfigs = (
  rulesets: Ruleset[],
  reposRuleSuitesListBySha: boolean,
): Array<QueryFieldConfig<InsightsFilter>> => [
  {providerKey: 'user', filterKey: 'actor', getValue: extractAsUser},
  timePeriodQueryField,
  {
    providerKey: 'ruleset',
    filterKey: 'ruleset',
    getValue: (values: Array<{displayName?: string}>) => rulesets.find(r => r.name === values[0]?.displayName),
  },
  {providerKey: 'rule-status', filterKey: 'ruleStatus'},
  {providerKey: 'evaluate-status', filterKey: 'evaluateStatus'},
  repositoryQueryField,
  orgQueryField,
  ...(reposRuleSuitesListBySha ? [{providerKey: 'sha', filterKey: 'afterOid' as const}] : []),
]

type InsightsFilterBar2Props = {
  filter: InsightsFilter
  sourceType: SourceType
  businessSlug?: string
  rulesets: Ruleset[]
}

export const InsightsFilterBar2: FC<InsightsFilterBar2Props> = ({filter, sourceType, businessSlug, rulesets}) => {
  const reposRuleSuitesListBySha = useFeatureFlag('repos_rule_suites_list_by_sha')

  const filterProviders = useMemo(() => {
    const providers: FilterProvider[] = [
      new EvaluateStatusFilterProvider(),
      new RulesetFilterProvider(rulesets),
      new UserFilterProvider(),
      new TimePeriodFilterProvider(),
      new RuleStatusFilterProvider(),
    ]

    if (sourceType === 'repository' && reposRuleSuitesListBySha) {
      providers.unshift(new ShaFilterProvider())
    }

    if (sourceType === 'organization') {
      providers.unshift(new RepositoryFilterProvider({filterTypes: singleValueFilterTypes}))
    }

    if (sourceType === 'enterprise' && businessSlug) {
      providers.unshift(new OrgFilterProvider({businessSlug, filterTypes: singleValueFilterTypes}))
    }

    return providers
  }, [rulesets, sourceType, reposRuleSuitesListBySha, businessSlug])

  const queryFieldConfigs = useMemo(
    () => createInsightsQueryFieldConfigs(rulesets, reposRuleSuitesListBySha),
    [rulesets, reposRuleSuitesListBySha],
  )

  return (
    <GenericFilterBar2<InsightsFilter>
      id="rules-insights-filter"
      label="Filter rules insights"
      filter={filter}
      fieldConfigs={insightsFieldConfigs}
      queryFieldConfigs={queryFieldConfigs}
      filterProviders={filterProviders}
      getIndexPath={insightsIndexPath}
    />
  )
}

try{ InsightsFilterBar2.displayName ||= 'InsightsFilterBar2' } catch {}